import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, Typography, useMediaQuery,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Tooltip,
} from "@mui/material";
import {Add, Delete, Edit, Image} from "@mui/icons-material";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import SideBar from "../Componant/sidebar/Sidebar";
import {listPalettes, deletePalette} from "../Utils/palettesApi";

const SWATCH_KEYS = ['dotColor', 'backgroundColor', 'cornerSquareColor', 'cornerDotColor'];

function PaletteSwatch({style}) {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            {SWATCH_KEYS.map((key) => (
                <Box key={key} sx={{
                    width: 20, height: 20, borderRadius: '4px',
                    backgroundColor: style[key] || '#cccccc',
                    border: '1px solid rgba(0,0,0,0.15)',
                }}/>
            ))}
            {style.logo && (
                <Tooltip title="Palette avec logo">
                    <Image fontSize="small" sx={{ml: 0.5, color: 'text.secondary'}}/>
                </Tooltip>
            )}
        </Stack>
    );
}

function PalettesPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();
    const navigate = useNavigate();

    const [palettes, setPalettes] = useState([]);
    const [loading, setLoading] = useState(true);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    const refresh = () => {
        setLoading(true);
        listPalettes()
            .then(setPalettes)
            .catch((error) => console.error('Erreur lors du chargement des palettes:', error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    const handleDelete = async (palette) => {
        if (!window.confirm(`Supprimer la palette "${palette.name}" ?`)) return;

        try {
            await deletePalette(palette.id);
            refresh();
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            alert('Erreur lors de la suppression de la palette');
        }
    };

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: {xs: 2, sm: 4}, margin: '0 auto', width: '100%'}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}}>
                            <Typography variant="h4">Palettes</Typography>
                            <Button variant="contained" color="primary" startIcon={<Add/>} onClick={() => navigate('/palettes/new')}>
                                Nouvelle palette
                            </Button>
                        </Stack>

                        <Box sx={{px: {xs: 0, sm: 2}}}>
                            {loading ? (
                                <Typography>Chargement...</Typography>
                            ) : palettes.length === 0 ? (
                                <Typography color="text.secondary">
                                    Aucune palette pour le moment. Créez-en une pour réutiliser un style entre vos QR codes.
                                </Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Nom</TableCell>
                                                <TableCell>Aperçu</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {palettes.map((palette) => (
                                                <TableRow key={palette.id} hover>
                                                    <TableCell sx={{wordBreak: 'break-word'}}>{palette.name}</TableCell>
                                                    <TableCell><PaletteSwatch style={palette.style}/></TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Modifier">
                                                            <IconButton onClick={() => navigate(`/palettes/${palette.id}`)}>
                                                                <Edit fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Supprimer">
                                                            <IconButton onClick={() => handleDelete(palette)}>
                                                                <Delete fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default PalettesPage;
